import { useEffect } from 'react'
import { RouteData, POI, Photo } from '@cya-routes/shared'
import { Comment } from '../../../types/comments'

interface UseMapRenderersProps {
  routes: RouteData[]
  selectedRouteId: string
  masterRoute: RouteData | null | undefined
  visiblePOIs: POI[]
  sortedPhotos: Photo[]
  showPhotos: boolean
  selectedPhoto: Photo | null
  comments?: Comment[]
  showComments?: boolean
  selectedComment?: Comment | null
  renderersInitialized: boolean
  routeRenderer: React.MutableRefObject<any>
  poiRenderer: React.MutableRefObject<any>
  photoRenderer: React.MutableRefObject<any>
  commentRenderer?: React.MutableRefObject<any>
}

/**
 * Custom hook that manages rendering of routes, POIs, and photos on the map
 * Consolidates all rendering effects into a single hook
 */
export function useMapRenderers({
  routes,
  selectedRouteId,
  masterRoute,
  visiblePOIs,
  sortedPhotos,
  showPhotos,
  selectedPhoto,
  comments,
  showComments,
  selectedComment,
  renderersInitialized,
  routeRenderer,
  poiRenderer,
  photoRenderer,
  commentRenderer
}: UseMapRenderersProps): void {
  // Render routes
  useEffect(() => {
    if (routeRenderer.current && renderersInitialized) {
      routeRenderer.current.renderRoutes(routes, selectedRouteId || undefined, masterRoute || undefined)
    }
  }, [routes, selectedRouteId, masterRoute, renderersInitialized, routeRenderer])

  // Render POIs with category filtering
  useEffect(() => {
    console.log(`🎨 useMapRenderers POI effect - renderersInitialized: ${renderersInitialized}, visiblePOIs: ${visiblePOIs.length}`)
    if (poiRenderer.current && renderersInitialized) {
      console.log(`🎨 Calling poiRenderer.renderPOIs with ${visiblePOIs.length} POIs`)
      poiRenderer.current.renderPOIs(visiblePOIs)
    }
  }, [visiblePOIs, renderersInitialized, poiRenderer])

  // Render photos (using memoized sorted photos)
  useEffect(() => {
    if (photoRenderer.current && renderersInitialized) {
      if (showPhotos) {
        photoRenderer.current.renderPhotos(sortedPhotos, selectedPhoto?.id)
      } else {
        // Hide all photos
        photoRenderer.current.renderPhotos([])
      }
    }
  }, [sortedPhotos, renderersInitialized, showPhotos, selectedPhoto?.id, photoRenderer])

  // Render comments
  useEffect(() => {
    if (commentRenderer?.current && renderersInitialized && comments) {
      if (showComments) {
        commentRenderer.current.renderComments(comments, selectedComment?.id)
      } else {
        // Hide all comments
        commentRenderer.current.clearMarkers()
      }
    }
  }, [comments, renderersInitialized, showComments, selectedComment?.id, commentRenderer])
}